'use client';

import { API_ROUTES, useApiInvalidate } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useClientConfig } from '@shared/utils';
import { useCallback, type ComponentProps, type ReactNode } from 'react';
import { LotteryLayout } from '../../../lotteries/LotteryLayout/LotteryLayout';
import type { LotteryName } from '../../../lottery-purchases/types';
import { KioskBenefits, LotteryLogo, NextDraw, PrizeTables, ResultsPageHeader, YourNumbersSection, type YourNumbersTicket } from '../../components';
import { isDrawingTicketsLoading, useDrawingTickets } from '../../hooks/useDrawingTickets/useDrawingTickets';
import { LiveDrawProvider, useLiveDrawStage } from '../../liveDraw';
import { getDrawnNumberCount, toNumberList, type DrawnNumbers } from '../../liveDraw/getRevealedNumbers';
import type { LiveDrawSlot } from '../../liveDraw/resolveLiveDrawStage';
import { PrizeTablesSkeleton } from '../../skeletons/PrizeTablesSkeleton';
import type { Drawing, DrawingsResponse, ResultsConfig } from '../../types';
import { hasUsablePrizeOdds } from '../../utils';

type ResultsPageLayoutProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
  currentResult: Drawing | null;
  betUrl: string;
  config?: ResultsConfig;
  header: ReactNode;
  backFallbackUrl?: string;
  headerTitle?: string;
  actions?: ReactNode;
  /** Optional trailing content in {@link ResultsPageHeader} (stacked under xl, right-aligned at xl+). */
  pageHeaderActions?: ReactNode;
  /** When false, Your Numbers does not wait on an unseen live-draw replay. */
  isLiveDrawView?: boolean;
  /** The results list, whose cadence lets live draw place a drawing neither of its endpoints reports yet. */
  drawings?: DrawingsResponse;
};

type ResultsPageBodyProps = Pick<ResultsPageLayoutProps, 'lotteryName' | 'currentResult' | 'betUrl' | 'config' | 'children'> & {
  /** The player's tickets for this drawing; fetched once in {@link ResultsPageLayout} and shared. */
  tickets: YourNumbersTicket[];
  isLoading: boolean;
  /**
   * Full drawn set for archive matching, including the Results tab. Left unset on the Live Draw tab
   * so Your Numbers keeps matching against the sequencer prefix.
   */
  revealedNumbers?: DrawnNumbers;
};

/** Archive slips keep their completed footer. The live path marks completion inside the sequence. */
function withCompletedDraw(tickets: YourNumbersTicket[], isDrawComplete: boolean): YourNumbersTicket[] {
  if (!isDrawComplete) {
    return tickets;
  }

  return tickets.map(ticket => ({ ...ticket, isDrawComplete: true }));
}

function showsYourNumbers(config: ResultsConfig | undefined) {
  return Boolean(config?.enableLiveDrawView || config?.showYourNumbers);
}

/** The selected drawing's full result, which is what archive matching highlights against. */
function toArchiveRevealedNumbers(result: Drawing): DrawnNumbers {
  const numbers = toNumberList(result.numbers);

  if (!result.extraNumbers) {
    return { numbers };
  }

  return { numbers, extraNumbers: toNumberList(result.extraNumbers) };
}

/**
 * Balls for Your Numbers on the Results tab.
 *
 * The Live Draw tab leaves this unset: an explicit `revealedNumbers` prop wins over the sequencer,
 * so passing the archive set there would freeze matching on the finished draw while balls are
 * still landing.
 */
function toResultsTabRevealedNumbers(isLiveDrawView: boolean, currentResult: Drawing | null): DrawnNumbers | undefined {
  if (isLiveDrawView || currentResult === null || getDrawnNumberCount(currentResult) === 0) {
    return undefined;
  }

  return toArchiveRevealedNumbers(currentResult);
}

/** Countdown and ball clips. The Results tab holds Your Numbers matches until this finishes. */
function isLiveDrawRevealInProgress(kind: LiveDrawSlot['kind']) {
  return kind === 'draw_starting' || kind === 'countdown_go' || kind === 'drawing_numbers';
}

function ResultsPageBody({ lotteryName, currentResult, betUrl, config, tickets, isLoading, revealedNumbers, children }: ResultsPageBodyProps) {
  const { currency } = useClientConfig();
  const kioskBenefits = config?.hideKiosk ? null : <KioskBenefits namespace="lotteries.results.kiosk" />;
  // The numbers can publish before the prize breakdown does. An undrawn placeholder has neither, and keeps its empty tables.
  const isAwaitingOdds = getDrawnNumberCount(currentResult) > 0 && !hasUsablePrizeOdds(currentResult?.odds);

  return (
    <LotteryLayout
      className="mt-8"
      sidePanelContent={
        <div className="flex flex-col gap-8">
          <NextDraw lotteryName={lotteryName} betUrl={betUrl} currency={currency} icon={config?.icon} data-track-container="Upcoming Lottery" />
          {kioskBenefits ? <div className="hidden xl:block">{kioskBenefits}</div> : null}
        </div>
      }
    >
      {showsYourNumbers(config) ? (
        <YourNumbersSection
          tickets={tickets}
          betUrl={betUrl}
          lotteryName={lotteryName}
          isLoading={isLoading}
          revealedNumbers={revealedNumbers}
          data-track-container="Your Numbers"
        />
      ) : null}
      {currentResult && !isAwaitingOdds ? (
        <PrizeTables
          data-testid="prize-tables"
          data={currentResult.odds ?? []}
          lotteryName={lotteryName}
          currency={currency}
          drawingDate={currentResult.drawingDate}
          extraGames={currentResult.extraGames}
          config={config}
          additionalDrawingInfo={currentResult.additionalDrawingInfo}
          data-track-container="Prize Breakdown"
        />
      ) : (
        <PrizeTablesSkeleton lotteryName={lotteryName} />
      )}
      {kioskBenefits ? <div className="xl:hidden">{kioskBenefits}</div> : null}
      <div className="flex justify-center">
        <LotteryLogo lotteryName={lotteryName} />
      </div>
      {children}
    </LotteryLayout>
  );
}

type ResultsPageFrameProps = Omit<ResultsPageLayoutProps, 'isLiveDrawView' | 'drawings'> & {
  tickets: YourNumbersTicket[];
  isLoading: boolean;
  revealedNumbers?: DrawnNumbers;
};

/** Stable empty slip for the markets that never fetch tickets, so the body does not see a new array each render. */
const NO_TICKETS: YourNumbersTicket[] = [];

/** The page itself. Identical either way — live draw only changes which tickets it is handed. */
function ResultsPageFrame({
  lotteryName,
  currentResult,
  betUrl,
  config,
  header,
  headerTitle,
  backFallbackUrl,
  actions,
  pageHeaderActions,
  tickets,
  isLoading,
  revealedNumbers,
  children,
  ...props
}: ResultsPageFrameProps) {
  return (
    <div {...props} data-track-feature="Lottery Results" data-track-container="Results" data-track-product={lotteryName}>
      <ResultsPageHeader lotteryName={lotteryName} backFallbackUrl={backFallbackUrl} actions={actions ?? pageHeaderActions} title={headerTitle} />
      {header}
      <ResultsPageBody
        lotteryName={lotteryName}
        currentResult={currentResult}
        betUrl={betUrl}
        config={config}
        tickets={tickets}
        isLoading={isLoading}
        revealedNumbers={revealedNumbers}
      >
        {children}
      </ResultsPageBody>
    </div>
  );
}

/**
 * The live-draw path: owns the sequence — and with it the tickets for the drawing on screen — and the provider.
 *
 * Split from {@link ResultsPageLayout} so a lottery without live draw never subscribes to the
 * clock. `useCurrentTime` throws without an `SsrTimeProvider`, so reading it unconditionally made
 * that provider a hard requirement of every results page — including markets that have no live
 * draw at all, and whose value was discarded anyway.
 */
function LiveDrawResultsPage({ isLiveDrawView = true, drawings, ...frameProps }: ResultsPageLayoutProps) {
  const { lotteryName, currentResult } = frameProps;
  const { invalidateQueriesMatchingPath } = useApiInvalidate();

  const handleSequenceComplete = useCallback(() => {
    invalidateQueriesMatchingPath(API_ROUTES.LOTTERIES.PURCHASES);
    invalidateQueriesMatchingPath(API_ROUTES.LOTTERIES.PURCHASED_DRAWING_TICKETS.split(':drawingId')[0]!);
    invalidateQueriesMatchingPath(API_ROUTES.LOTTERIES.PURCHASED_TICKET_DETAILS.split('?')[0]!);
    // Fires as the balls land, usually ahead of the prize breakdown. This picks up the new drawing
    // without waiting on the results poll, which may have backed off to a minute by then.
    invalidateQueriesMatchingPath(API_ROUTES.LOTTERIES.LOTTERY_RESULTS.replace(':lotteryName', lotteryName));
  }, [invalidateQueriesMatchingPath, lotteryName]);

  const sequence = useLiveDrawStage(lotteryName, { replaySource: currentResult, drawings, isLiveDrawView, onSequenceComplete: handleSequenceComplete });
  const isViewingLiveReveal =
    !isLiveDrawView &&
    !sequence.isReplay &&
    currentResult?.drawingId !== undefined &&
    currentResult.drawingId === sequence.liveDraw?.drawingId &&
    isLiveDrawRevealInProgress(sequence.stage.slot.kind);
  const tickets = isViewingLiveReveal ? sequence.tickets.map(ticket => ({ ...ticket, isDrawComplete: false })) : sequence.tickets;

  // Wraps the whole page so the panel, Your Numbers and prizes share one sequence; the header tab
  // can then switch without remounting it.
  return (
    <LiveDrawProvider lotteryName={lotteryName} sequence={sequence} isLiveDrawView={isLiveDrawView}>
      <ResultsPageFrame
        {...frameProps}
        tickets={tickets}
        isLoading={sequence.isTicketsLoading}
        revealedNumbers={isViewingLiveReveal ? { numbers: [] } : toResultsTabRevealedNumbers(isLiveDrawView, currentResult)}
      />
    </LiveDrawProvider>
  );
}

/**
 * Archive path: tickets and highlights follow the dropdown drawing.
 *
 * Kept off the live-draw clock so a sequence in progress cannot replace the selected drawing.
 */
function ArchiveResultsPage(frameProps: ResultsPageLayoutProps) {
  const { lotteryName, currentResult } = frameProps;
  const { isLoading: isSessionLoading } = useSession();
  const hasDrawnNumbers = getDrawnNumberCount(currentResult) > 0;
  const drawingId = currentResult?.drawingId;
  const ticketsQuery = useDrawingTickets({
    lotteryName,
    drawingId,
    hasDrawnNumbers,
    drawnNumbers: hasDrawnNumbers && currentResult ? currentResult : undefined,
  });
  const tickets = ticketsQuery.data?.tickets ?? [];

  return (
    <ResultsPageFrame
      {...frameProps}
      tickets={withCompletedDraw(tickets, hasDrawnNumbers)}
      isLoading={isDrawingTicketsLoading(ticketsQuery, { hasDrawing: drawingId !== undefined, isSessionLoading })}
      revealedNumbers={hasDrawnNumbers && currentResult ? toArchiveRevealedNumbers(currentResult) : undefined}
    />
  );
}

function ResultsPageLayout({ isLiveDrawView = true, drawings, ...props }: ResultsPageLayoutProps) {
  if (props.config?.enableLiveDrawView) {
    return <LiveDrawResultsPage {...props} drawings={drawings} isLiveDrawView={isLiveDrawView} />;
  }

  if (props.config?.showYourNumbers) {
    return <ArchiveResultsPage {...props} />;
  }

  return <ResultsPageFrame {...props} tickets={NO_TICKETS} isLoading={false} />;
}

export { ResultsPageLayout };
export type { ResultsPageLayoutProps };
